import {
  daysInMonth,
  isoDateFromParts,
  isoDateInZone,
  partsInZone,
  zonedPartsToUtc,
  type Task,
} from '@cleartask/shared';
import { useTimeZone } from '../../session.js';

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** Domingo primeiro, como a grade do mes (§8ab). Uma letra: o mini-mes e estreito. */
const CABECALHO = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

interface YearViewProps {
  year: number;
  byDay: Map<string, Task[]>;
  now: number;
  /** O servidor cortou a resposta: ha tarefas do ano que a grade nao recebeu. */
  truncado?: boolean;
  /** Inicio do dia clicado; a tela decide para qual visao ir. */
  onPickDay: (dayStart: number) => void;
}

/**
 * Visao "Ano": doze mini-meses, como no TickTick.
 *
 * Responde "quando estou livre / carregado" no horizonte longo, por isso so
 * MARCA o dia com tarefa (um ponto) em vez de listar titulos — num quadrado de
 * 24 px nenhum titulo caberia, e um "+3" em cada dia viraria ruido. O detalhe
 * fica a um clique: o dia leva a visao Dia daquela data.
 *
 * Sem alvo de soltura: arrastar para um dia de 24 px seria um gesto de
 * precisao que ninguem acerta no celular.
 */
export function YearView({ year, byDay, now, truncado = false, onPickDay }: YearViewProps) {
  const timeZone = useTimeZone();
  const hoje = isoDateInZone(timeZone, now);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {/*
        Dia sem ponto quer dizer "livre" — a menos que a resposta tenha sido
        cortada. Dito em texto, e nao escondido: um ano com mais tarefas do que
        cabe num pedido marcaria dias ocupados como vazios sem ninguem saber.
      */}
      {truncado ? (
        <p role="status" className="mb-3 text-xs text-text-mut">
          Mostrando parte do ano — há tarefas demais para um só pedido.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
        {MESES.map((nome, indice) => {
          const mes = indice + 1;
          const total = daysInMonth(year, mes);
          // Celulas vazias antes do dia 1, para o dia cair na coluna certa da
          // semana que comeca no domingo.
          const vazios = partsInZone(timeZone, zonedPartsToUtc(timeZone, { year, month: mes, day: 1 }))
            .weekday;

          return (
            <section key={mes} aria-label={`${nome} de ${year}`}>
              <h2 className="mb-1 px-1 text-sm font-semibold capitalize">{nome}</h2>

              <div className="grid grid-cols-7 text-center">
                {CABECALHO.map((letra, i) => (
                  <span key={i} aria-hidden="true" className="text-[10px] text-text-mut">
                    {letra}
                  </span>
                ))}

                {Array.from({ length: vazios }, (_, i) => (
                  <span key={`v${i}`} aria-hidden="true" />
                ))}

                {Array.from({ length: total }, (_, i) => {
                  const dia = i + 1;
                  const iso = isoDateFromParts(year, mes, dia);
                  const qtd = byDay.get(iso)?.length ?? 0;
                  const ehHoje = iso === hoje;

                  return (
                    <button
                      key={iso}
                      type="button"
                      data-date={iso}
                      aria-label={`${dia} de ${nome}${
                        qtd > 0 ? `, ${qtd} ${qtd === 1 ? 'tarefa' : 'tarefas'}` : ''
                      }`}
                      aria-current={ehHoje ? 'date' : undefined}
                      onClick={() =>
                        onPickDay(zonedPartsToUtc(timeZone, { year, month: mes, day: dia }))
                      }
                      className="flex h-7 flex-col items-center justify-center rounded-sm text-xs tabular-nums hover:bg-surface-2"
                    >
                      <span
                        className={
                          ehHoje
                            ? 'grid h-5 min-w-5 place-items-center rounded-full bg-brand-blue px-1 font-medium text-white'
                            : ''
                        }
                      >
                        {dia}
                      </span>
                      {/* Ponto fixo em altura: com ou sem tarefa a linha nao pula. */}
                      <span
                        aria-hidden="true"
                        className={[
                          'h-1 w-1 rounded-full',
                          qtd > 0 ? 'bg-brand-blue' : 'bg-transparent',
                        ].join(' ')}
                      />
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
